import { useState } from "react";
import { IconArrowDownChevronSmall } from "@synthesis/shared-icons";
import { Button, Checkbox, Popover, ScrollArea, SearchBar } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";
import { ALL_RAILWAYS_VALUE, getMonitoringRailwayLabel, getMonitoringRailwayQuery, RAILWAY_SHARES } from "../monitoring-page.lib";

type MonitoringRailwayFilterProps = {
  value: string;
  onChange: (value: string) => void;
};

export const MonitoringRailwayFilter = ({ value, onChange }: MonitoringRailwayFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState<string[]>([]);
  const allSelected = draft.length === RAILWAY_SHARES.length;
  const appliedRoads = getMonitoringRailwayQuery(value);
  const appliedSelection: readonly string[] = appliedRoads ?? RAILWAY_SHARES.map(({ id }) => id);
  const hasChanges = draft.length !== appliedSelection.length || draft.some((id) => !appliedSelection.includes(id));
  const label = getMonitoringRailwayLabel(value);
  const filteredRoads = RAILWAY_SHARES.filter((road) => road.name.toLocaleLowerCase("ru-RU").includes(search.trim().toLocaleLowerCase("ru-RU")));
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setDraft(getMonitoringRailwayQuery(value) ?? RAILWAY_SHARES.map(({ id }) => id));
      setSearch("");
    }
    setIsOpen(open);
  };

  return (
    <Popover
      open={isOpen}
      setIsOpen={handleOpenChange}
      side="bottom"
      align="end"
      isArrow={false}
      className="w-[15.125rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl py-1"
      trigger={
        <span
          className={cn(
            "paragraph-sm text-type-minor bg-neutrals-95 border-neutrals-90 hover:bg-neutrals-90 flex h-8 w-[15.125rem] items-center justify-between gap-2 rounded-[10px] border-2 py-1 pl-3 pr-2 text-left",
            isOpen && "border-neutrals-80 bg-neutrals-90",
          )}
        >
          <span className="sr-only">Фильтр по дорогам: </span>
          <span className="min-w-0 flex-1 truncate">{label}</span>
          <IconArrowDownChevronSmall className="text-type-light size-5 shrink-0" aria-hidden />
        </span>
      }
    >
      <div className="flex flex-col">
        <div className="px-2.5 py-1">
          <SearchBar placeholder="Поиск" size="sm" value={search} onChange={setSearch} debounceTime={0} clearable />
        </div>
        <ScrollArea direction="vertical" rootClassName="min-h-0" className="max-h-80">
          <div className="hover:bg-neutrals-95 flex h-10 items-center px-2.5">
            <Checkbox
              size="sm"
              checked={allSelected}
              label={<span className="paragraph-sm text-type-common">Все дороги</span>}
              onChange={(checked) => setDraft(checked ? RAILWAY_SHARES.map(({ id }) => id) : [])}
              className="w-full [&>label]:flex-1"
            />
          </div>
          {filteredRoads.map((road) => (
            <div key={road.id} className="hover:bg-neutrals-95 flex h-10 items-center px-2.5">
              <Checkbox
                size="sm"
                checked={draft.includes(road.id)}
                label={<span className="paragraph-sm text-type-common">{road.name}</span>}
                onChange={(checked) => setDraft((previous) => (checked ? [...previous, road.id] : previous.filter((id) => id !== road.id)))}
                className="w-full [&>label]:flex-1"
              />
            </div>
          ))}
          {filteredRoads.length === 0 && <p className="paragraph-sm text-type-minor px-2.5 py-4">Ничего не найдено</p>}
        </ScrollArea>
        <div className="flex gap-2 px-2.5 py-1">
          <Button
            type="button"
            size="sm"
            theme="primary"
            className="flex-1"
            disabled={draft.length === 0 || !hasChanges}
            onClick={() => {
              onChange(
                allSelected
                  ? ALL_RAILWAYS_VALUE
                  : RAILWAY_SHARES.filter(({ id }) => draft.includes(id))
                      .map(({ id }) => id)
                      .join(","),
              );
              setIsOpen(false);
            }}
          >
            Применить
          </Button>
          <Button
            type="button"
            size="sm"
            theme="secondary"
            className="flex-1"
            disabled={!appliedRoads && allSelected}
            onClick={() => {
              onChange(ALL_RAILWAYS_VALUE);
              setIsOpen(false);
            }}
          >
            Сбросить
          </Button>
        </div>
      </div>
    </Popover>
  );
};
